import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { MemberContribution } from '@/api/statistics';
import { useChartTheme } from '@/hooks/useChartTheme';
import { useTranslation } from 'react-i18next';

interface MemberContributionsChartProps {
  data: MemberContribution[];
  currency: string;
}

function CustomTooltip({ active, payload, label, currency }: any) {
  if (!active || !payload?.length) return null;
  const minorUnits = getMinorUnitsSync(currency);
  return (
    <div className="rounded-lg bg-surface-primary px-3 py-2 shadow-lg ring-1 ring-border-primary">
      <p className="text-xs font-medium text-text-secondary mb-1">{label}</p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {formatMinorAmount(entry.value, currency, minorUnits)}
        </p>
      ))}
    </div>
  );
}

const MAX_VISIBLE_HEIGHT = 300;
const ROW_HEIGHT = 50;
const CHART_MARGINS = { top: 5, right: 20, bottom: 0, left: 5 };
const FOOTER_MARGINS = { top: 0, right: 20, bottom: 5, left: 5 };

export function MemberContributionsChart({ data, currency }: MemberContributionsChartProps) {
  const { t } = useTranslation();
  const minorUnits = getMinorUnitsSync(currency);
  const ct = useChartTheme();
  const formatTick = (value: number) =>
    formatMinorAmount(value, currency, minorUnits, undefined, true);

  const chartData = useMemo(
    () => data.map(m => ({ name: m.displayName, paid: m.paid, owed: m.owed })),
    [data]
  );

  const maxValue = useMemo(
    () => Math.max(...data.map(m => Math.max(m.paid, m.owed)), 0),
    [data]
  );

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-text-quaternary">
        {t('web:memberContributionsChart.noMemberData')}
      </div>
    );
  }

  const chartHeight = Math.max(200, data.length * ROW_HEIGHT);
  const needsScroll = chartHeight > MAX_VISIBLE_HEIGHT;

  if (!needsScroll) {
    return (
      <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
        <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('groups:stats.memberContributions')}</h3>
        <ResponsiveContainer width="100%" height={chartHeight + 40}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} tickFormatter={formatTick} domain={[0, maxValue]} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: ct.tickSecondary }} axisLine={false} tickLine={false} width={100} />
            <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: ct.cursorFill }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="paid" name="Paid" fill="#4f46e5" radius={[0, 4, 4, 0]} barSize={16} />
            <Bar dataKey="owed" name="Owed" fill="#a5b4fc" radius={[0, 4, 4, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('groups:stats.memberContributions')}</h3>

      {/* Scrollable bar area */}
      <div className="overflow-y-auto" style={{ maxHeight: MAX_VISIBLE_HEIGHT }}>
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={CHART_MARGINS}>
              <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
              <XAxis type="number" hide domain={[0, maxValue]} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: ct.tickSecondary }} axisLine={false} tickLine={false} width={100} />
              <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: ct.cursorFill }} />
              <Bar dataKey="paid" name="Paid" fill="#4f46e5" radius={[0, 4, 4, 0]} barSize={16} />
              <Bar dataKey="owed" name="Owed" fill="#a5b4fc" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Static footer: X-axis */}
      <ResponsiveContainer width="100%" height={30}>
        <BarChart data={chartData} layout="vertical" margin={FOOTER_MARGINS}>
          <XAxis type="number" tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} tickFormatter={formatTick} domain={[0, maxValue]} />
          <YAxis type="category" dataKey="name" hide width={100} />
          <Bar dataKey="paid" hide />
          <Bar dataKey="owed" hide />
        </BarChart>
      </ResponsiveContainer>

      {/* Static legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-text-secondary">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: '#4f46e5' }} />
          {t('expenses:participant.paid')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: '#a5b4fc' }} />
          {t('web:memberContributionsChart.owed')}
        </span>
      </div>
    </div>
  );
}
